import { UserProfileData } from '@/types/userProfile';

// Define required fields for each role
const REQUIRED_FIELDS_BY_ROLE = {
  player: [
    'first_name',
    'last_name', 
    'current_sport',
    'position',
    'skill_level',
    'age_group'
  ],
  coach: [
    'first_name',
    'last_name',
    'primary_sport',
    'years_coaching',
    'coaching_style'
  ],
  'team-owner': [
    'first_name',
    'last_name',
    'phone'
  ],
  venue: [
    'venue_name',
    'venue_address',
    'venue_type',
    'operating_hours',
    'booking_contact_email'
  ],
  fan: [
    'first_name',
    'last_name'
  ],
  parent: [
    'first_name',
    'last_name',
    'phone'
  ],
  creative: [
    'first_name',
    'last_name'
  ],
  support: [
    'first_name',
    'last_name',
    'support_role',
    'experience_level'
  ],
  admin: [
    'first_name',
    'last_name'
  ]
};

// Optional but recommended fields for bonus completion percentage
const RECOMMENDED_FIELDS_BY_ROLE = {
  player: [
    'bio',
    'profile_photo_url',
    'height',
    'weight',
    'years_playing',
    'achievements'
  ],
  coach: [
    'bio',
    'profile_photo_url',
    'coaching_certifications',
    'specializations',
    'previous_teams'
  ],
  'team-owner': [
    'bio',
    'profile_photo_url'
  ],
  venue: [
    'venue_description',
    'profile_photo_url',
    'venue_capacity',
    'facilities_available'
  ],
  fan: [
    'bio',
    'profile_photo_url',
    'preferred_sports'
  ],
  parent: [
    'bio',
    'profile_photo_url'
  ],
  creative: [
    'bio',
    'profile_photo_url'
  ],
  support: [
    'bio',
    'profile_photo_url',
    'certifications',
    'years_experience'
  ],
  admin: [
    'bio',
    'profile_photo_url'
  ]
};

export interface ProfileValidationResult {
  isComplete: boolean;
  completionPercentage: number;
  missingRequiredFields: string[];
  missingRecommendedFields: string[];
  roleSpecificMessage: string;
}

export const validateProfileCompletion = (
  profile: UserProfileData | any,
  userRole: string
): ProfileValidationResult => {
  if (!profile || !userRole) {
    return {
      isComplete: false,
      completionPercentage: 0,
      missingRequiredFields: [],
      missingRecommendedFields: [],
      roleSpecificMessage: 'Profile data or role not available'
    };
  }

  const normalizedRole = userRole.toLowerCase();
  const requiredFields = REQUIRED_FIELDS_BY_ROLE[normalizedRole] || [];
  const recommendedFields = RECOMMENDED_FIELDS_BY_ROLE[normalizedRole] || [];

  // Check required fields
  const missingRequiredFields = requiredFields.filter(field => {
    const value = profile[field];
    return !value || (Array.isArray(value) && value.length === 0) || value === '';
  });

  // Check recommended fields
  const missingRecommendedFields = recommendedFields.filter(field => {
    const value = profile[field];
    return !value || (Array.isArray(value) && value.length === 0) || value === '';
  });

  const totalFields = requiredFields.length + recommendedFields.length;
  const completedFields = (requiredFields.length - missingRequiredFields.length) + 
                         (recommendedFields.length - missingRecommendedFields.length);
  
  const completionPercentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  const isComplete = missingRequiredFields.length === 0;

  const roleSpecificMessage = getRoleSpecificMessage(normalizedRole, isComplete, missingRequiredFields);

  return {
    isComplete,
    completionPercentage,
    missingRequiredFields,
    missingRecommendedFields,
    roleSpecificMessage
  };
};

const getRoleSpecificMessage = (role: string, isComplete: boolean, missingFields: string[]): string => {
  if (isComplete) {
    const messages = {
      player: 'Your player profile is complete! You can now join teams and participate in games.',
      coach: 'Your coaching profile is complete! You can now manage teams and connect with players.',
      'team-owner': 'Your team owner profile is complete! You can now create and manage teams.',
      venue: 'Your venue profile is complete! You can now host games and accept bookings.',
      fan: 'Your fan profile is complete! You can now follow teams and engage with content.',
      parent: 'Your parent profile is complete! You can now connect with your children and follow their activities.',
      creative: 'Your creative profile is complete! You can now showcase your work and connect with teams.',
      support: 'Your support profile is complete! You can now offer your services to teams and players.',
      admin: 'Your admin profile is complete!'
    };
    return messages[role] || 'Your profile is complete!';
  }

  if (missingFields.length === 0) return '';

  const fieldNames = missingFields.map(field => {
    const fieldMappings = {
      first_name: 'First Name',
      last_name: 'Last Name',
      current_sport: 'Current Sport',
      position: 'Position',
      skill_level: 'Skill Level',
      age_group: 'Age Group',
      primary_sport: 'Primary Sport',
      years_coaching: 'Years Coaching',
      coaching_style: 'Coaching Style',
      venue_name: 'Venue Name',
      venue_address: 'Venue Address',
      venue_type: 'Venue Type',
      operating_hours: 'Operating Hours',
      booking_contact_email: 'Booking Contact Email',
      phone: 'Phone Number',
      support_role: 'Support Role',
      experience_level: 'Experience Level'
    };
    return fieldMappings[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  });

  return `Please complete these required fields: ${fieldNames.join(', ')}`;
};

export const getFieldLabel = (fieldName: string): string => {
  const fieldMappings = {
    first_name: 'First Name',
    last_name: 'Last Name',
    current_sport: 'Current Sport',
    position: 'Position',
    skill_level: 'Skill Level',
    age_group: 'Age Group',
    primary_sport: 'Primary Sport',
    years_coaching: 'Years Coaching',
    coaching_style: 'Coaching Style',
    venue_name: 'Venue Name',
    venue_address: 'Venue Address',
    venue_type: 'Venue Type',
    operating_hours: 'Operating Hours',
    booking_contact_email: 'Booking Contact Email',
    phone: 'Phone Number',
    support_role: 'Support Role',
    experience_level: 'Experience Level',
    bio: 'Bio',
    profile_photo_url: 'Profile Photo',
    height: 'Height',
    weight: 'Weight',
    years_playing: 'Years Playing',
    achievements: 'Achievements',
    coaching_certifications: 'Coaching Certifications',
    specializations: 'Specializations',
    previous_teams: 'Previous Teams',
    venue_description: 'Venue Description',
    venue_capacity: 'Venue Capacity',
    facilities_available: 'Available Facilities',
    preferred_sports: 'Preferred Sports',
    certifications: 'Certifications',
    years_experience: 'Years of Experience'
  };
  
  return fieldMappings[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};